import React, { useState, useEffect, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import DashboardLayout from "../../layouts/DashboardLayout";
import DashboardSummaryStrip from "../../components/DashboardSummaryStrip/DashboardSummaryStrip";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import localeText from "../../../../locale";
import OrderService from "../../../../service/OrderService";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import { formatPrice } from "../../../../utilities/formatPrice";
import { formatDate } from "../../../../utilities/formatDate";
import "../FinancialHub/components/FinancialTransactionsTable/FinancialTransactionsTable.css";
import "./StoreOrdersPage.css";
import { Tooltip } from "primereact/tooltip";
import { PARTNER_MAX_PRODUCTS, SELLER_MAX_PRODUCTS } from "../../../../utilities/constants";
import ProductTypeModal from "./components/ProductTypeModal/ProductTypeModal";

const orderService = new OrderService();
const getUserInfoService = new GetUserInfoService();

const isStoreSalesEnabled = (plan, userInfo) => {
  if (plan !== "Store") return true;
  const profilePlan = (userInfo?.plan || userInfo?.subscriptionType || "").toString();
  return (
    userInfo?.showCase === true ||
    userInfo?.showCase === "true" ||
    userInfo?.showcase === true ||
    userInfo?.showcase === "true" ||
    /startseller|seller/i.test(profilePlan)
  );
};

const LAST_ORDERS_LIMIT = 8;

const StorePanelPage = ({
  userTypeInfo,
  userInfo,
  currency,
  productsRefreshTrigger,
}) => {
  const history = useHistory();
  const token = localStorage.getItem("__extraconomy_token");
  const username = localStorage.getItem("__extraconomy_username");

  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [showTypeModal, setShowTypeModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingOrders(true);
      try {
        const res = await orderService.getSales(token, username);
        if (cancelled) return;
        if (res?.messageCode === 200001 && Array.isArray(res?.result)) {
          const list = res.result.filter((o) => o.status !== "PENDING");
          setOrders(list);
        } else {
          setOrders([]);
        }
      } catch {
        if (!cancelled) setOrders([]);
      } finally {
        if (!cancelled) setLoadingOrders(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, username]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await getUserInfoService.getProducts(token, username);
        if (cancelled) return;
        if (res?.messageCode === 200001 && Array.isArray(res?.result)) {
          setProducts(res.result.filter((p) => p.status !== "DELETED"));
        } else {
          setProducts([]);
        }
      } catch {
        if (!cancelled) setProducts([]);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token, username, productsRefreshTrigger]);

  const kpis = useMemo(() => {
    const totalOperations = orders.length;
    const registeredArticles = products.length;
    const revenue = orders
      .filter((o) => o.status === "RECEIVED")
      .reduce((acc, o) => acc + (Number(o.amountTotal ?? o.amountOrder ?? 0) || 0), 0);
    const pendingOperations = orders.filter(
      (o) => o.status === "PAID" || o.status === "SENT",
    ).length;
    return {
      totalOperations,
      registeredArticles,
      revenue,
      pendingOperations,
    };
  }, [orders, products]);

  const lastEightOrders = useMemo(() => {
    const byDate = [...orders].sort((a, b) => new Date(b.date) - new Date(a.date));
    return byDate.slice(0, LAST_ORDERS_LIMIT);
  }, [orders]);

  const handleNewProduct = () => {
    if (!disableProductActions && !storeUpgradeRequired) setShowTypeModal(true);
  };

  const maxProductsForPlan = useMemo(() => {
    if (userTypeInfo === "Seller") return SELLER_MAX_PRODUCTS;
    if (userTypeInfo === "Partner") return PARTNER_MAX_PRODUCTS;
    return null;
  }, [userTypeInfo]);
  const hasReachedProductLimit =
    typeof maxProductsForPlan === "number" && products.length >= maxProductsForPlan;
  const storeUpgradeRequired = !isStoreSalesEnabled(userTypeInfo, userInfo);
  const disableProductActions = hasReachedProductLimit || storeUpgradeRequired;
  const productLimitReachedTooltip = hasReachedProductLimit
    ? (localeText("productLimitReached") || "").replace("{limit}", String(maxProductsForPlan))
    : undefined;

  const statusConfig = {
    RECEIVED: { text: "received", color: "var(--theme-primary, #1DAF61)" },
    SENT: { text: "sent", color: "#42A5F5" },
    PAID: { text: "paid", color: "#F7B005" },
    CANCELED: { text: "canceled", color: "#eb4034" },
  };

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />
      <div className="flex align-items-center justify-content-between flex-wrap gap-2">
        <div className="flex align-items-center gap-5 mb-4 mt-4">
          <div
            className="border-circle p-3"
            style={{
              border: "1px solid var(--theme-border, #EBEBEB)",
              width: "54px",
              height: "54px",
            }}
          >
            <i
              className="pi pi-shopping-bag text-xl"
              style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
            />
          </div>
          <div>
            <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
              {localeText("storePanelTitle")}
            </h2>
            <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
              {localeText("storePanelSubtitle")}
            </p>
          </div>
        </div>

        <Tooltip target=".new-product-btn-wrapper" />
        <span
          className="new-product-btn-wrapper"
          data-pr-tooltip={productLimitReachedTooltip}
          data-pr-position="top"
          style={{
            display: "inline-block",
            cursor: disableProductActions ? "not-allowed" : "auto",
          }}
        >
          <Button
            label={localeText("newProduct")}
            icon="pi pi-plus"
            className="border-round-md border-none p-no-outline"
            style={{
              backgroundColor: disableProductActions ? "#ccc" : "var(--theme-primary, #1DAF61)",
              color: "#fff",
              pointerEvents: disableProductActions ? "none" : "auto",
            }}
            onClick={storeUpgradeRequired ? undefined : handleNewProduct}
            disabled={disableProductActions}
          />
        </span>
      </div>
      {(hasReachedProductLimit || storeUpgradeRequired) && (
        <div
          className="flex align-items-center justify-content-between flex-wrap gap-3 border-round p-3 mt-3 mb-4"
          style={{
            border: "1px solid var(--theme-border, #EBEBEB)",
            backgroundColor: "color-mix(in srgb, var(--theme-border) 24%, transparent)",
          }}
        >
          <p className="m-0 text-sm" style={{ color: "var(--theme-text-muted)" }}>
            {storeUpgradeRequired
              ? localeText("storeActivateStartSellerWarning")
              : (localeText("storeProductsLimitWarning") || "").replace(
                  "{limit}",
                  String(maxProductsForPlan),
                )}
          </p>
          <Link to="/dashboard-upgrade" className="text-sm font-semibold no-underline">
            {localeText("storeActivateStartSellerCta")}
          </Link>
        </div>
      )}
      <DashboardSummaryStrip
        items={[
          {
            label: localeText("storeOperations"),
            value: kpis.totalOperations.toLocaleString("pt-BR"),
            helper: localeText("sinceStart"),
          },
          {
            label: localeText("storeRegisteredArticles"),
            value: kpis.registeredArticles.toLocaleString("pt-BR"),
            helper: localeText("sinceStart"),
          },
          {
            label: localeText("storeRevenue"),
            value: <>€ {formatPrice(kpis.revenue)}</>,
            helper: localeText("sinceStart"),
          },
          {
            label: localeText("storePendingOperations"),
            value: kpis.pendingOperations,
            helper: localeText("awaitingProcessing"),
            helperColor: "var(--theme-primary)",
          },
        ]}
        className="store-panel-summary"
      />

      <div className="flex flex-column gap-3">
        <div className="flex align-items-center justify-content-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text)" }}>
            {localeText("latestOrders")}
          </h2>

          <Link to="/dashboard-store/orders">
            <Button
              label={localeText("allOrders")}
              icon="pi pi-history"
              className="border-round-md border-none p-no-outline text-sm font-medium"
              style={{
                backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                color: "var(--theme-text-muted)",
              }}
            />
          </Link>
        </div>

        <div className="financial-transactions-table">
          <div className="overflow-hidden">
            <DataTable
              value={lastEightOrders}
              loading={loadingOrders}
              paginator={false}
              emptyMessage={
                <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
                  <div
                    className="border-round flex align-items-center justify-content-center"
                    style={{
                      width: "120px",
                      height: "120px",
                      backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                    }}
                  >
                    <i className="pi pi-inbox text-6xl text-400" />
                  </div>
                  <p className="m-0" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
                    {localeText("noOperationsRecords")}
                  </p>
                </div>
              }
              onRowClick={(e) =>
                history.push(`/dashboard-store/orders/details?orderId=${e.data.id}`)
              }
              // PrimeReact 10 passa [rowData, options] como array para o rowClassName
              rowClassName={(args) =>
                (Array.isArray(args) ? args[0] : args)?.status === "PAID"
                  ? "cursor-pointer store-order-row--pending-action"
                  : "cursor-pointer"
              }
              className="financial-transactions-datatable"
            >
              <Column
                field="id"
                header="ID"
                sortable
                body={(row) => <span className="font-medium">{row.id}</span>}
              />
              <Column
                field="date"
                header={localeText("date")}
                sortable
                body={(row) => formatDate(row.date)}
              />
              <Column
                field="status"
                header={localeText("status")}
                body={(row) => {
                  const cfg = statusConfig[row.status] || statusConfig.PAID;
                  return (
                    <span className="flex align-items-center gap-2">
                      <span
                        className="border-circle"
                        style={{ width: "8px", height: "8px", backgroundColor: cfg.color }}
                      />
                      {localeText(cfg.text)}
                    </span>
                  );
                }}
              />
              <Column
                header={localeText("customer")}
                body={(row) => row.buyer?.username ?? row.buyer?.firstname ?? "—"}
              />
              <Column
                header={localeText("product")}
                body={(row) =>
                  row.itens?.[0]?.product?.title ?? row.itens?.[0]?.product?.description ?? "—"
                }
              />
              <Column
                header={localeText("value")}
                body={(row) => {
                  const val = Number(row.amountTotal ?? row.amountOrder ?? 0) || 0;
                  return <span className="font-medium">€ {formatPrice(val)}</span>;
                }}
              />
              <Column
                header=""
                body={() => <i className="pi pi-chevron-right text-400" />}
                style={{ width: "40px" }}
              />
            </DataTable>
          </div>
        </div>
      </div>
      <ProductTypeModal visible={showTypeModal} onHide={() => setShowTypeModal(false)} />
    </DashboardLayout>
  );
};

StorePanelPage.defaultProps = {
  productsRefreshTrigger: 0,
};

export default StorePanelPage;
